import { InjectionToken } from '@angular/core';
import {
  Observable,
  Subject,
  distinctUntilChanged,
  fromEvent,
  map,
  share,
  takeUntil,
  tap,
} from 'rxjs';
import {
  ArrowHelper,
  Camera,
  Event,
  Intersection,
  Object3D,
  Raycaster,
  RaycasterParameters,
  Scene,
  Vector2,
} from 'three';

/** Event names dispatched on hovered collidable objects (see {@link SimlabCollider.enableHover}). */
export const HOVER_ENTER_EVENT = 'onHoverEnter' as const;
export const HOVER_LEAVE_EVENT = 'onHoverLeave' as const;

export type HoverIntersection = Intersection<Object3D<Event>> | undefined;
export const SIMLAB_COLLIDER = new InjectionToken<SimlabCollider>(
  'Simlab collider'
);
export class SimlabCollider {
  private readonly _raycaster: Raycaster = new Raycaster();
  private readonly _pointer: Vector2 = new Vector2();
  private _arrowHelper?: ArrowHelper;
  private readonly _destroyer: Subject<void> = new Subject<void>();
  readonly pointerDownCollisionListener$ = fromEvent<PointerEvent>(
    this._window,
    'pointerdown'
  ).pipe(
    map((event) => this._pointerCollision(event)));

  readonly pointerMoveCollisionListener$ = fromEvent<PointerEvent>(
    this._window,
    'pointermove'
  ).pipe(map((event) => this._pointerCollision(event)));

  objectsCb?: () => Object3D<Event>[];

  private _hoverSubscription?: { unsubscribe: () => void };
  private _hovered: Object3D<Event> | undefined;
  /**
   * Emits the collidable object under the pointer (or `undefined`) every time it changes.
   * Active only after {@link enableHover} was called.
   */
  readonly hoverChange$: Observable<HoverIntersection> =
    this.pointerMoveCollisionListener$.pipe(
      map((intersects) => this._firstCollidable(intersects)),
      distinctUntilChanged((a, b) => a?.object === b?.object),
      share()
    );

  constructor(
    private readonly _camera: Camera,
    private readonly _scene: Scene,
    private readonly _recursive: boolean = false,
    private readonly _window: Window = window,
    private readonly _canvas?: HTMLElement
  ) {
    this._raycaster.layers.enable(3);
    this._pointerDownObserverver();
  }

  get raycaster() {
    return this._raycaster;
  }

  addArrowHelper() {
    this._arrowHelper = new ArrowHelper(
      this._raycaster.ray.direction,
      this._raycaster.ray.origin,
      30,
      0xff0000
    );

    this._scene.add(this._arrowHelper);
  }

  destroy() {
    this.disableHover();
    this._destroyer.next();
  }

  /**
   * Starts dispatching `onHoverEnter` (with the intersection `point`) and `onHoverLeave`
   * on collidable objects as the pointer moves over the scene. Opt-in, because it
   * raycasts on every `pointermove`.
   */
  enableHover() {
    if (this._hoverSubscription) return;
    this._hoverSubscription = this.hoverChange$
      .pipe(
        tap((intersection) => this._dispatchHover(intersection)),
        takeUntil(this._destroyer)
      )
      .subscribe();
  }

  disableHover() {
    this._hoverSubscription?.unsubscribe();
    this._hoverSubscription = undefined;
    this._dispatchHover(undefined);
  }

  private _dispatchHover(intersection: HoverIntersection) {
    const next = intersection?.object;
    if (this._hovered === next) return;
    this._hovered?.dispatchEvent({ type: HOVER_LEAVE_EVENT });
    this._hovered = next;
    if (next && intersection) {
      next.dispatchEvent({
        type: HOVER_ENTER_EVENT,
        point: intersection.point.clone(),
      } as Event);
    }
  }

  private _firstCollidable(
    intersects: Intersection<Object3D<Event>>[]
  ): HoverIntersection {
    for (let i = 0; i < intersects.length; i++) {
      if ('collision' in intersects[i].object) return intersects[i];
    }
    return undefined;
  }
  set params(params: RaycasterParameters) {
    this._raycaster.params = {
      ...this._raycaster.params,
      ...params,
    };
  }
  get layers() {
    return this._raycaster.layers;
  }

  addObjectToWorld$() {
    return this.pointerMoveCollisionListener$.pipe(
      takeUntil(this.pointerDownCollisionListener$)
    );
  }

  private _pointerDownObserverver() {
    this.pointerDownCollisionListener$
      .pipe(
        tap((intersects) => this._globalCollision(intersects)),
        takeUntil(this._destroyer)
      )
      .subscribe();
  }

  private _pointerCollision(event: PointerEvent) {
    this._setPointer(event);
    this._raycaster.setFromCamera(this._pointer, this._camera);

    if (this._arrowHelper) {
      const origin = this._raycaster.ray.origin;
      const direction = this._raycaster.ray.direction;

      this._arrowHelper.position.copy(origin);
      this._arrowHelper.setDirection(direction);
    }
    return this._raycaster.intersectObjects(
      this.objectsCb ? this.objectsCb() : this._scene.children,
      this._recursive
    );
  }
  private _globalCollision(
    intersects: Intersection<Object3D<import('three').Event>>[]
  ) {
    for (let i = 0; i < intersects.length; i++) {
      const object = intersects[i].object;
      if (!('collision' in object)) continue;
      object.dispatchEvent({ type: 'onClick' });
      break;
    }
  }

  private _setPointer(event: PointerEvent) {
    if (this._canvas) {
      const rect = this._canvas.getBoundingClientRect();

      this._pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this._pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    } else {
      this._pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
      this._pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;
    }
  }
}
